/**
 * SecurityCenter — operator's live security posture page (Palantir edition).
 *
 * Bloomberg-terminal layout: bracketed section headers, monospace data
 * rows, status pills, and ASCII fill indicators.  Same data as before —
 * /admin/security, /admin/backups, /admin/security/findings — just
 * presented in the new intelligence-brief language.
 */
import React, { useEffect, useState } from 'react';
import {
  ShieldCheck, Lock, Database, Mail, KeyRound, RefreshCw, Server,
  Archive, Loader2, Radar, Activity, BookOpen, Clock,
} from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

export default function SecurityCenter() {
  const [status, setStatus] = useState(null);
  const [backups, setBackups] = useState([]);
  const [findings, setFindings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [harunRunning, setHarunRunning] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: s }, { data: b }, { data: f }] = await Promise.all([
        api.get('/admin/security'),
        api.get('/admin/backups'),
        api.get('/admin/security/findings'),
      ]);
      setStatus(s);
      setBackups(b.backups);
      setFindings(f.findings || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load security status.');
    } finally { setLoading(false); }
  };
  /* eslint-disable -- v6 react-hooks/set-state-in-effect false-positive on plain async fetches */
  useEffect(() => { load(); }, []);
  /* eslint-enable */

  const triggerHarun = async () => {
    setHarunRunning(true);
    try {
      const { data } = await api.post('/admin/security/harun/run-now');
      toast.success(`Harun scanned ${data.scanned_event_count} events. Threat: ${data.threat_level}.`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Harun scan failed.');
    } finally { setHarunRunning(false); }
  };

  const triggerBackup = async () => {
    setRunning(true);
    try {
      const { data } = await api.post('/admin/backups/run-now');
      if (data.ok) {
        toast.success(`Backup created (${(data.size_bytes/1024/1024).toFixed(2)} MB)`);
        await load();
      } else {
        toast.error(`Backup failed: ${data.error || 'unknown'}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Backup request failed.');
    } finally { setRunning(false); }
  };

  if (loading || !status) return (
    <div className="pal-card pal-grid-bg p-5">
      <span className="pal-bracket">[SECURITY · LOADING…]</span>
      <div className="pal-progress mt-3 text-xs"><span className="pal-fill">▓▓▓▒▒</span><span className="pal-empty">░░░░░░░░░░░</span></div>
    </div>
  );

  const dawudFindings = findings.filter((f) => f.source === 'dawud').length;
  const harunLatest = findings.filter((f) => f.source === 'harun').slice(0, 1)[0];

  return (
    <div className="space-y-5" data-testid="security-center-page">
      {/* ── Header ── */}
      <div className="pal-card pal-grid-bg p-5 flex items-start gap-4">
        <div
          className="h-12 w-12 rounded-md flex items-center justify-center shrink-0 border"
          style={{
            backgroundColor: 'rgba(174,82,234,0.10)',
            color: 'var(--ve-violet-bright)',
            borderColor: 'rgba(174,82,234,0.40)',
          }}
        >
          <ShieldCheck size={22} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="pal-bracket">[SECURITY · DR · LIVE POSTURE]</div>
          <h1 className="display text-2xl mt-0.5">Your data, protected at every layer.</h1>
          <p className="text-sm mt-1 max-w-2xl opacity-70">
            Live posture report from the running cluster. Backups, encryption, TLS, and the
            session store are checked on every page load.
          </p>
        </div>
        <button
          onClick={load}
          data-testid="security-refresh"
          className="px-3 py-2 text-[10px] uppercase tracking-[0.22em] rounded border pal-mono font-bold border-white/10 text-slate-300 hover:bg-white/5 flex items-center gap-1.5 dark:border-white/10 dark:text-slate-300 dark:hover:bg-white/5"
        >
          <RefreshCw size={12} /> REFRESH
        </button>
      </div>

      {/* ── 4 Posture Cards ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* TLS */}
        <PostureCard
          label="[IN-TRANSIT · TLS]"
          icon={Lock}
          status={status.in_transit.https ? 'ok' : 'alert'}
          statusLabel={status.in_transit.https ? 'ACTIVE' : 'DOWN'}
        >
          <Row k="HTTPS" v={status.in_transit.https ? 'ENABLED' : 'DISABLED'} ok={status.in_transit.https} />
          <Row k="TLS · TERMINATION" v={status.in_transit.tls_termination || '—'} ok={!!status.in_transit.tls_termination} />
          {status.in_transit.notes && (
            <p className="pal-mono text-[10px] opacity-55 italic mt-2">{status.in_transit.notes}</p>
          )}
        </PostureCard>

        {/* AT-REST */}
        <PostureCard
          label="[AT-REST · ENCRYPTION]"
          icon={KeyRound}
          status={status.at_rest.field_level_encryption.configured ? 'ok' : 'warn'}
          statusLabel={status.at_rest.field_level_encryption.configured ? 'AES-256-GCM' : 'NOT CONFIG'}
        >
          <Row k="FIELD CIPHER" v={status.at_rest.field_level_encryption.algorithm || 'NOT CONFIGURED'}
               ok={status.at_rest.field_level_encryption.configured} />
          <Row k="PASSWORD HASH"
               v={`${status.at_rest.password_hash.algorithm} · rounds=${status.at_rest.password_hash.rounds}`}
               ok />
          <Row k="KEY SOURCE" v={`env: ${status.at_rest.field_level_encryption.key_env_var}`} ok />
        </PostureCard>

        {/* SMTP */}
        <PostureCard
          label="[EMAIL · SMTP]"
          icon={Mail}
          status={status.smtp.configured ? 'ok' : 'warn'}
          statusLabel={status.smtp.configured ? 'CONNECTED' : 'NOT CONFIG'}
        >
          <Row k="SMTP" v={status.smtp.configured ? 'CONNECTED' : 'NOT CONFIGURED'} ok={status.smtp.configured} />
          <Row k="ACTIVE SESSIONS" v={String(status.session_store.active_sessions)} ok />
        </PostureCard>

        {/* BACKUPS */}
        <PostureCard
          label="[DATABASE · BACKUPS]"
          icon={Database}
          status={status.backups.last_backup ? 'ok' : 'warn'}
          statusLabel={status.backups.last_backup ? `${status.backups.total_backups} ARCHIVES` : 'NO ARCHIVES'}
        >
          <Row k="SCHEDULE" v={status.backups.schedule_utc} ok />
          <Row k="RETAIN" v={`${status.backups.retain_days} DAYS`} ok />
          <Row k="ON DISK" v={String(status.backups.total_backups)} ok />
          {status.backups.last_backup && (
            <Row k="LATEST" v={`${status.backups.last_backup.filename} · ${status.backups.last_backup.size_mb || 0} MB`} ok mono />
          )}
          <button
            onClick={triggerBackup}
            disabled={running}
            data-testid="security-trigger-backup"
            className="mt-3 px-3 py-2 text-[10px] uppercase tracking-[0.22em] font-bold rounded pal-mono transition-transform hover:translate-y-[-1px] disabled:opacity-50 bg-[var(--ve-violet)] text-black hover:bg-[var(--ve-violet-bright)] flex items-center gap-1.5"
          >
            {running ? <Loader2 size={12} className="animate-spin" /> : <Archive size={12} />}
            RUN BACKUP NOW →
          </button>
        </PostureCard>
      </div>

      {/* ── Atlas KMS slot ── */}
      <div className="pal-card pal-card-live pal-grid-bg p-5" data-testid="security-atlas-kms-card">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-2">
            <Database size={13} className="text-[var(--ve-violet-bright)]" />
            <span className="pal-bracket">[ATLAS KMS · WHOLE-DB ENCRYPTION]</span>
          </div>
          {status.at_rest.database_at_rest?.status === 'enabled' ? (
            <span className="pal-status pal-status-ok" data-testid="atlas-kms-enabled-badge">ENABLED</span>
          ) : (
            <span className="pal-status pal-status-warn" data-testid="atlas-kms-pending-badge">PENDING · TIER UPGRADE</span>
          )}
        </div>

        <p className="pal-mono text-[10px] opacity-65 leading-relaxed mb-4">
          Customer-managed key in AWS KMS · drop-in slot — set env vars and restart to enable.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className="pal-bracket text-[9px] mb-2">[LIVE STATUS]</div>
            <Row k="PROVIDER" v={status.at_rest.database_at_rest?.provider || '—'}
                 ok={status.at_rest.database_at_rest?.status === 'enabled'} />
            <Row k="ALGORITHM" v={status.at_rest.database_at_rest?.algorithm || '—'}
                 ok={status.at_rest.database_at_rest?.status === 'enabled'} />
            {status.at_rest.database_at_rest?.key_id && (
              <Row k="CMK ARN" v={status.at_rest.database_at_rest.key_id} ok mono small />
            )}
            {status.at_rest.database_at_rest?.role_arn && (
              <Row k="IAM ROLE" v={status.at_rest.database_at_rest.role_arn} ok mono small />
            )}
          </div>
          <div>
            <div className="pal-bracket text-[9px] mb-2">
              [{status.at_rest.database_at_rest?.status === 'enabled' ? 'CONFIGURED ENV' : 'DROP-IN ENV · SET TO ENABLE'}]
            </div>
            {Object.entries(status.at_rest.database_at_rest?.expected_env || {}).map(([key, env]) => {
              const isMissing = (status.at_rest.database_at_rest?.missing_env || []).includes(key);
              return (
                <div key={env} className="pal-row" data-testid={`atlas-kms-env-${key}`}>
                  <span className="pal-mono text-[10px] truncate">{env}</span>
                  <span className={`pal-status ${isMissing ? 'pal-status-alert' : 'pal-status-ok'}`}>
                    {isMissing ? 'UNSET' : 'SET'}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {status.at_rest.database_at_rest?.notes && (
          <div className="mt-4 pal-mono text-[10px] opacity-70 italic leading-relaxed border-l-2 border-[var(--ve-violet)] pl-3 py-1.5">
            {status.at_rest.database_at_rest.notes}
          </div>
        )}

        <div className="mt-4 flex flex-wrap items-center gap-2 pal-mono text-[10px] opacity-70">
          <BookOpen size={11} className="text-[var(--ve-violet-bright)]" />
          <span>RUNBOOK ·</span>
          <code className="px-1.5 py-0.5 bg-[rgba(174,82,234,0.10)] border border-[rgba(174,82,234,0.25)] rounded text-[10px]">
            {status.at_rest.database_at_rest?.migration_runbook || '/app/aws/ATLAS_KMS_MIGRATION.md'}
          </code>
        </div>
      </div>

      {/* ── AI Security Sentinels ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="pal-card pal-grid-bg p-5" data-testid="security-dawud-card">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <ShieldCheck size={13} className="text-[var(--ve-violet-bright)]" />
              <span className="pal-bracket">[DAWUD · INJECTION SENTINEL]</span>
            </div>
            <span className="pal-status pal-status-ok">ACTIVE</span>
          </div>
          <p className="text-xs opacity-70 leading-relaxed">
            Heuristic regex scan on every <span className="pal-mono">/api/*</span> request.
            Path + query + headers. Blocks SQL/NoSQL/cmd/XSS/SSRF/path-traversal
            (risk ≥ 80). Medium-risk requests logged for Harun.
          </p>
          <div className="pal-row mt-3">
            <span className="pal-key">FINDINGS · 14D</span>
            <span className="pal-val">{dawudFindings}</span>
          </div>
          <div className="pal-mono text-[10px] opacity-55 mt-2" dir="rtl">دَاوُود</div>
        </div>

        <div className="pal-card pal-card-live pal-grid-bg p-5" data-testid="security-harun-card">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Radar size={13} className="text-[var(--ve-violet-bright)]" />
              <span className="pal-bracket">[HARUN · THREAT HUNTER]</span>
            </div>
            <button
              onClick={triggerHarun}
              disabled={harunRunning}
              data-testid="security-harun-runnow"
              className="px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] font-bold rounded border pal-mono border-[rgba(174,82,234,0.40)] text-[var(--ve-violet-bright)] hover:bg-[rgba(174,82,234,0.10)] disabled:opacity-50 flex items-center gap-1.5"
            >
              {harunRunning ? <Loader2 size={11} className="animate-spin" /> : <Activity size={11} />}
              SCAN NOW
            </button>
          </div>
          <p className="text-xs opacity-70 leading-relaxed">
            Nightly at 03:00 UTC. Reviews 24h of <span className="pal-mono">audit_logs</span> for
            login-failed spikes, role-escalation chains, after-hours approver activity, and
            mass-export bursts.
          </p>
          {harunLatest && (
            <div className="mt-3 rounded border border-[rgba(174,82,234,0.18)] bg-[rgba(174,82,234,0.04)] p-3" data-testid="harun-latest">
              <div className="flex items-center gap-2 mb-2">
                <span className={`pal-status ${
                  harunLatest.threat_level === 'red' ? 'pal-status-alert'
                  : harunLatest.threat_level === 'amber' ? 'pal-status-warn'
                  : 'pal-status-ok'
                }`}>
                  {String(harunLatest.threat_level).toUpperCase()}
                </span>
                <span className="pal-mono text-[10px] opacity-70">
                  {harunLatest.scanned_event_count} EVENTS · {harunLatest.anomalies?.length || 0} ANOMALY
                </span>
              </div>
              {(harunLatest.anomalies || []).slice(0, 3).map((a, i) => (
                <div key={i} className="pal-mono text-[10px] opacity-80 mt-1">
                  <span className="opacity-50 mr-1">[{String(a.severity).toUpperCase()}]</span>
                  {a.summary}
                </div>
              ))}
            </div>
          )}
          <div className="pal-mono text-[10px] opacity-55 mt-3" dir="rtl">هَارُون</div>
        </div>
      </div>

      {/* ── Backup history ── */}
      <div className="pal-card pal-grid-bg p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Server size={13} className="text-[var(--ve-violet-bright)]" />
            <span className="pal-bracket">[BACKUP · HISTORY]</span>
          </div>
          <span className="pal-mono text-[10px] tabular-nums opacity-70">N={backups.length}</span>
        </div>
        {backups.length === 0 && (
          <div className="text-sm opacity-60 py-6 text-center pal-mono">
            NO ARCHIVES YET · CLICK &ldquo;RUN BACKUP NOW&rdquo; ABOVE
          </div>
        )}
        <div className="rounded-md border border-[rgba(174,82,234,0.18)] overflow-hidden">
          {backups.map((b, i) => (
            <div
              key={b.filename}
              className={`flex items-center gap-3 px-3 py-2.5 pal-mono text-[11px] ${
                i % 2 ? 'bg-[rgba(174,82,234,0.02)]' : ''
              } ${i < backups.length - 1 ? 'border-b border-dashed border-[rgba(174,82,234,0.15)]' : ''}`}
              data-testid={`backup-row-${b.filename}`}
            >
              <Archive size={12} className="opacity-50 shrink-0" />
              <span className="truncate flex-1">{b.filename}</span>
              <span className="opacity-70 tabular-nums w-16 text-right">{b.size_mb} MB</span>
              <span className="opacity-50 tabular-nums flex items-center gap-1 text-[10px]">
                <Clock size={10} /> {new Date(b.created_at).toLocaleString()}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="pal-mono text-[10px] opacity-50 px-1">
        DR · RUNBOOK <span className="opacity-70">/app/docs/disaster_recovery.md</span>
        <span className="mx-2 opacity-40">·</span>
        AWS · CUTOVER <span className="opacity-70">/app/backend/scripts/aws_cutover.sh</span>
      </div>
    </div>
  );
}

/* ── Posture card (4 grid items at top) ─────────────────────────────── */
function PostureCard({ label, icon: Icon, status, statusLabel, children }) {
  return (
    <div className="pal-card pal-grid-bg p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon size={13} className="text-[var(--ve-violet-bright)]" />
          <span className="pal-bracket">{label}</span>
        </div>
        <span className={`pal-status pal-status-${status}`}>{statusLabel}</span>
      </div>
      {children}
    </div>
  );
}

/* ── Bloomberg-style data row ───────────────────────────────────────── */
function Row({ k, v, ok = true, mono = false, small = false }) {
  return (
    <div className="pal-row">
      <span className="pal-key pal-mono">{k}</span>
      <span className="flex items-center gap-2 min-w-0 max-w-[60%]">
        <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${ok ? 'bg-emerald-500' : 'bg-rose-500'}`}
              style={{ boxShadow: ok ? '0 0 6px rgba(16,185,129,0.6)' : '0 0 6px rgba(244,63,94,0.6)' }} />
        <span className={`pal-val ${small ? 'text-[10px]' : 'text-[11px]'} truncate ${mono ? 'pal-mono' : ''}`}>
          {v}
        </span>
      </span>
    </div>
  );
}
